import type { LengthPercentage } from "./elements.ts";

/**
 * The radius scale: the `rounded-*` steps, stated as multiples of the theme's
 * `--radius`.
 *
 * The one place the scale is written down. The stylesheet emits it as CSS
 * variables and the parser resolves it to lengths, and a scale that lived in
 * two places would drift the moment either moved — with the symptom being an
 * element whose stored radius disagrees with what the browser paints.
 */

/**
 * Every step, as a multiple of `--radius`.
 *
 * Tailwind's stock scale IS this ramp: at a `--radius` of `0.5rem` these
 * multiples come out as 0.125 / 0.25 / 0.375 / 0.5 / 0.75 / 1 / 1.5 / 2 rem —
 * exactly the table the parser used to hardcode. That is why the built-in
 * themes are tuned to `0.5rem`: wiring the scale up moves nothing that already
 * exists, and the scale only starts to say something once a theme changes it.
 *
 * Multiplicative rather than shadcn's additive `calc(var(--radius) - 4px)`. The
 * two agree exactly at `0.5rem`, and the additive one goes NEGATIVE below it —
 * a theme asking for square corners would give `rounded-sm` a radius of -4px.
 */
export const RADIUS_SCALE_STEPS = {
  xs: 0.25,
  sm: 0.5,
  md: 0.75,
  lg: 1,
  xl: 1.5,
  "2xl": 2,
  "3xl": 3,
  "4xl": 4,
} as const;

export type RadiusScaleStepName = keyof typeof RADIUS_SCALE_STEPS;

/**
 * The two radius classes that are NOT steps.
 *
 * Tailwind compiles `rounded-none` to a literal `0` and `rounded-full` to
 * `calc(infinity * 1px)` rather than to a `--radius-*` variable, and neither
 * means anything different under a different theme — so both are px, and both
 * pass through the ramp untouched. They are named here so that a token covers
 * every non-arbitrary radius class, but they must never be emitted into the
 * `--radius-*` namespace.
 */
export const RADIUS_FIXED: Record<"none" | "full", LengthPercentage> = {
  none: { value: 0, unit: "px" },
  full: { value: 9999, unit: "px" },
};

/** A radius class that names a value rather than stating one. */
export type RadiusTokenName = RadiusScaleStepName | keyof typeof RADIUS_FIXED;

/**
 * The same set at runtime, derived from the two tables so they can't drift.
 *
 * Ordered by how round each one is — square to circle — which is the only
 * ordering that means anything to someone reading the scale as a scale. That
 * puts `none` at the top and `full` at the bottom rather than leaving both
 * stranded after `4xl` where the table split would have left them.
 */
export const RADIUS_TOKEN_NAMES = [
  "none",
  ...Object.keys(RADIUS_SCALE_STEPS),
  "full",
] as RadiusTokenName[];

/**
 * The `--radius` a theme that states none gets.
 *
 * `0.5rem`, not shadcn's `0.625rem`, because this is the value at which the
 * ramp above reproduces Tailwind's stock scale — see {@link RADIUS_SCALE_STEPS}.
 */
export const DEFAULT_THEME_RADIUS = "0.5rem";

/** {@link DEFAULT_THEME_RADIUS} as the model spells it. */
export const DEFAULT_RADIUS: LengthPercentage = { value: 0.5, unit: "rem" };

/**
 * Rounding as a scale rather than a length: the generator picks a step, where a
 * free length would let it state `47px`. The theme editor offers the same steps
 * first, and a custom base beside them. `balanced` is
 * {@link DEFAULT_THEME_RADIUS}, so a theme with nothing to say about rounding
 * looks like today's built-ins.
 */
export const THEME_ROUNDNESS = {
  square: "0rem",
  slight: "0.25rem",
  balanced: "0.5rem",
  soft: "0.75rem",
  round: "1rem",
} as const;

export type ThemeRoundness = keyof typeof THEME_ROUNDNESS;

export const THEME_ROUNDNESS_NAMES = Object.keys(
  THEME_ROUNDNESS,
) as ThemeRoundness[];

/** The step a radius base sits on, if it sits on one. */
export const themeRoundnessOf = (base: string): ThemeRoundness | undefined =>
  THEME_ROUNDNESS_NAMES.find((name) => THEME_ROUNDNESS[name] === base);

/**
 * The scale, for a given `--radius`.
 *
 * Each step is stated in the base's OWN unit — a `--radius` in rem gives a
 * scale in rem, one in px gives px. Resolving to px would put back the root
 * font size assumption that stating theme scales in rem exists to avoid, and
 * `themeScales.test.ts` compares this table against the compiler's own
 * `--radius-*` variables, which are rem.
 *
 * `none` and `full` are not steps and pass through unscaled.
 */
export const radiusScaleAt = (
  base: LengthPercentage,
): Record<RadiusTokenName, LengthPercentage> => {
  const scale = { ...RADIUS_FIXED } as Record<
    RadiusTokenName,
    LengthPercentage
  >;

  for (const [name, multiple] of Object.entries(RADIUS_SCALE_STEPS)) {
    scale[name as RadiusTokenName] = {
      value: roundScaleValue(base.value * multiple),
      unit: base.unit,
    } as LengthPercentage;
  }

  return scale;
};

/**
 * `0.375`, not `0.37500000000000006`.
 *
 * `0.5 * 0.75` is exact in binary but `1.25 * 0.75` is not, and the number ends
 * up compared for equality — against the compiler's own scale in
 * `themeScales.test.ts`, and corner against corner when the generator decides
 * whether four radii collapse to one class.
 */
const roundScaleValue = (value: number): number =>
  Math.round(value * 1e6) / 1e6;

/**
 * The base and the scale, as the declarations a stylesheet's `:root` carries.
 *
 * Each step stays a `calc()` over `var(--radius)` rather than a resolved
 * length, so a theme edit that moves `--radius` moves the whole scale with it
 * — including inside the shadcn components rendering in the same document,
 * which read these same variables.
 *
 * They are plain custom properties rather than a `@theme` block because
 * Tailwind already declares every one of these names: `rounded-md` compiles to
 * `var(--radius-md)` off its OWN theme, and an unlayered `:root` value is what
 * then wins. Declaring them here instead means the variables also exist at
 * browser level, which they did not while the ramp lived inside `@theme
 * inline` — a block whose namespaced variables Tailwind inlines and never
 * emits.
 */
export const radiusScaleDeclarations = (
  radius: string | undefined,
): string[] => [
  `--radius: ${radius || DEFAULT_THEME_RADIUS};`,
  ...Object.entries(RADIUS_SCALE_STEPS).map(
    ([name, multiple]) =>
      `--radius-${name}: ${
        multiple === 1 ? "var(--radius)" : `calc(var(--radius) * ${multiple})`
      };`,
  ),
];
